import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from '../utils/axiosInstance';
import { Button } from '@/components/ui/button';
import { PageLoader } from '@/components/ui/page-loader';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Pagination } from '@/components/ui/pagination';
import { usePagination } from '@/hooks/usePagination';
import SectionTextSettingsCard, { type SectionTextKey } from '@/components/SectionTextSettingsCard';
import ImageCropField from '@/components/ImageCropField';
import ServiceIconField from '@/components/ServiceIconField';
import { EMPTY_SERVICE_ICON, serviceIconPayload } from '@/config/serviceIcons';
import { Dropdown } from '@/components/ui/dropdown';
import { normalizeUrl, stripProtocol } from '@/utils/urlInput';
import { BASE_URL } from '@/config/constants';
import { toast } from 'sonner';
import { Pencil, Plus, Trash2 } from 'lucide-react';

import type { DragEndEvent } from '@dnd-kit/core';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

/**
 * ManagementSectionManager — shared management console UI for the five
 * management-only website sections (services, artist roster, testimonials,
 * clients, team). Parity with the Showcase manager: section-settings card (heading /
 * subtitle), drag & drop reordering with revert-on-failure, pagination,
 * image upload with crop, per-row visibility toggle and status labels.
 *
 * Each page configures this component with its API path, form fields and
 * columns; sections can then evolve one by one without re-implementing the
 * boilerplate. All API calls go through `axiosInstance` (JWT + X-Domain).
 */

export interface ManagerFieldDef {
  name: string;
  label: string;
  type: 'text' | 'textarea' | 'url' | 'switch' | 'socials' | 'image' | 'select' | 'serviceIcon';
  placeholder?: string;
  required?: boolean;
  maxLength?: number;
  rows?: number;
  helpText?: string;
  /** Image fields: crop aspect ratio. `null` = free (native) ratio. */
  aspectRatio?: number | null;
  /** Image fields: round crop (avatars). */
  circular?: boolean;
  /** Select fields: option list. */
  options?: { value: string; label: string }[];
  /** Select fields: map the stored item value to an option value. */
  fromValue?: (value: any) => string;
  /** Select fields: map the chosen option value to the stored payload value. */
  toValue?: (value: string) => unknown;
}

export interface ManagerColumnDef {
  name: string;
  label: string;
  render?: (item: any) => React.ReactNode;
}

interface ManagementSectionManagerProps {
  title: string;
  description: string;
  /** API base path, e.g. '/api/artist-roster' (tenant via X-Domain header). */
  apiPath: string;
  /** Section key for the section-settings (heading / subtitle) card. */
  sectionKey: SectionTextKey;
  /** Noun for dialogs/labels, e.g. 'artist' / 'testimonial'. */
  itemLabel: string;
  fields: ManagerFieldDef[];
  columns: ManagerColumnDef[];
  /** Optional per-row badge (e.g. roster auto rows → "synced from company"). */
  badge?: (item: any) => { label: string; tone?: 'default' | 'muted' } | null;
  /** Optional per-row visibility toggle. The Switch is checked = Active
   *  (system-wide convention); the server-side `/toggle-active` endpoint
   *  flips the underlying field (plain `isActive` or inverted `isHidden`)
   *  and responds with a unified `isActive` boolean. */
  toggleField?: { name: string };
  /** Return false to disable deletion (e.g. synced roster rows). */
  canDelete?: (item: any) => boolean;
  deleteDisabledReason?: string;
  emptyHint: string;
}

const toFormState = (fields: ManagerFieldDef[], item?: any): Record<string, any> => {
  const state: Record<string, any> = {};
  for (const field of fields) {
    if (field.type === 'serviceIcon') {
      state[field.name] = item?.[field.name] ?? { ...EMPTY_SERVICE_ICON };
    } else if (field.type === 'switch') {
      state[field.name] = item ? item[field.name] !== false : field.name !== 'isHidden';
    } else if (field.type === 'select' && field.fromValue) {
      state[field.name] = field.fromValue(item?.[field.name]);
    } else if (field.type === 'socials') {
      const socials = item?.[field.name];
      state[field.name] = Array.isArray(socials)
        ? socials.map((s: any) => `${s?.label || ''} | ${s?.url || ''}`).join('\n')
        : '';
    } else {
      state[field.name] = item?.[field.name] ?? '';
    }
  }
  return state;
};

const toPayload = (fields: ManagerFieldDef[], form: Record<string, any>): Record<string, any> => {
  const payload: Record<string, any> = {};
  for (const field of fields) {
    if (field.type === 'serviceIcon') {
      payload[field.name] = serviceIconPayload(form[field.name]);
    } else if (field.type === 'switch') {
      payload[field.name] = form[field.name] !== false;
    } else if (field.type === 'socials') {
      payload[field.name] = String(form[field.name] || '')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .slice(0, 12)
        .map((line) => {
          const [label, ...rest] = line.split('|');
          return { label: (label || '').trim(), url: rest.join('|').trim() };
        })
        .filter((s) => s.label || s.url);
    } else if (field.type === 'select') {
      const value = String(form[field.name] ?? '');
      if (field.required && !value) {
        throw new Error(`${field.label} is required`);
      }
      payload[field.name] = field.toValue ? field.toValue(value) : value;
    } else if (field.type === 'url') {
      const raw = String(form[field.name] ?? '').trim();
      if (field.required && !raw) {
        throw new Error(`${field.label} is required`);
      }
      // Stored values always carry the protocol, even when the field was
      // never touched after loading a legacy/un-prefixed value.
      const { url } = normalizeUrl(raw);
      payload[field.name] = url;
    } else {
      const value = String(form[field.name] ?? '').trim();
      if (field.required && !value) {
        throw new Error(`${field.label} is required`);
      }
      payload[field.name] = field.maxLength ? value.slice(0, field.maxLength) : value;
    }
  }
  return payload;
};

const ManagementSectionManager: React.FC<ManagementSectionManagerProps> = ({
  title,
  description,
  apiPath,
  sectionKey,
  itemLabel,
  fields,
  columns,
  badge,
  toggleField,
  canDelete,
  deleteDisabledReason,
  emptyHint,
}) => {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [form, setForm] = useState<Record<string, any>>(() => toFormState(fields));
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [urlSuggestions, setUrlSuggestions] = useState<Record<string, string | null>>({});
  const location = useLocation();
  const navigate = useNavigate();

  const load = async () => {
    try {
      const res = await axios.get(`${BASE_URL}${apiPath}`);
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err: any) {
      toast.error(err.response?.data?.message || `Failed to load ${itemLabel}s`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiPath]);

  const { paginate, paginationProps } = usePagination(items.length, {
    defaultPerPage: 10,
    resetDeps: [items.length],
  });
  const paginatedItems = paginate(items);

  const openCreate = () => {
    setEditing(null);
    setForm(toFormState(fields));
    setDialogOpen(true);
  };

  const openEdit = (item: any) => {
    setEditing(item);
    setForm(toFormState(fields, item));
    setDialogOpen(true);
  };

  // Auto-open the create dialog when navigated here from a dashboard card
  // (state.openAdd). Clears the navigation state so it doesn't re-trigger.
  useEffect(() => {
    if (!(location.state as { openAdd?: boolean } | null)?.openAdd) return;
    navigate(location.pathname, { replace: true, state: null });
    openCreate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = toPayload(fields, form);
      if (editing) {
        await axios.put(`${BASE_URL}${apiPath}/${editing._id}`, payload);
        toast.success(`${itemLabel} updated`);
      } else {
        await axios.post(`${BASE_URL}${apiPath}`, payload);
        toast.success(`${itemLabel} added`);
      }
      setDialogOpen(false);
      await load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || err?.message || `Failed to save ${itemLabel}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${BASE_URL}${apiPath}/${deleteTarget._id}`);
      toast.success(`${itemLabel} deleted`);
      setDeleteTarget(null);
      await load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || `Failed to delete ${itemLabel}`);
    }
  };

  const handleToggleActive = async (id: string) => {
    try {
      setTogglingId(id);
      const response = await axios.patch(`${BASE_URL}${apiPath}/${id}/toggle-active`);
      // The toggle endpoint returns the updated doc with a unified `isActive`
      // (effective visibility after the flip) so this works for both plain
      // and inverted fields without knowing their polarity.
      const updated = response.data;
      setItems((prev) => prev.map((it) => (it._id === id ? updated : it)));
      toast.success(`${title} ${updated.isActive ? 'shown' : 'hidden'} successfully!`);
    } catch (err: any) {
      toast.error(err.response?.data?.message || `Failed to toggle ${itemLabel} status`);
    } finally {
      setTogglingId(null);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((it) => it._id === active.id);
      const newIndex = items.findIndex((it) => it._id === over.id);
      const newItems = arrayMove(items, oldIndex, newIndex);
      setItems(newItems);
      try {
        await axios.post(`${BASE_URL}${apiPath}/reorder`, { items: newItems });
        toast.success(`${title} order updated`);
      } catch (err: any) {
        toast.error(err.response?.data?.message || 'Failed to update order');
        load(); // revert on failure
      }
    }
  };

  const renderField = (field: ManagerFieldDef) => {
    const value = form[field.name];
    switch (field.type) {
      case 'serviceIcon':
        return <ServiceIconField value={value} onChange={(icon) => setForm((prev) => ({ ...prev, [field.name]: icon }))} />;
      case 'textarea':
      case 'socials':
        return (
          <Textarea
            value={value ?? ''}
            rows={field.rows ?? (field.type === 'socials' ? 3 : 4)}
            maxLength={field.maxLength}
            placeholder={field.placeholder}
            onChange={(e) => setForm((prev) => ({ ...prev, [field.name]: e.target.value }))}
          />
        );
      case 'image':
        return (
          <ImageCropField
            value={value ?? ''}
            onChange={(url: string) => setForm((prev) => ({ ...prev, [field.name]: url }))}
            aspectRatio={field.aspectRatio === undefined ? 1 : field.aspectRatio}
            circularCrop={field.circular}
            maxSizeMB={2}
            hint={field.helpText || 'Upload and crop, or leave empty for a styled placeholder.'}
          />
        );
      case 'select':
        return (
          <Dropdown
            value={value ?? ''}
            options={field.options ?? []}
            onValueChange={(next) => setForm((prev) => ({ ...prev, [field.name]: String(next) }))}
          />
        );
      case 'url':
        return (
          <div className="space-y-2">
            <Input
              type="text"
              inputMode="url"
              value={stripProtocol(value ?? '')}
              placeholder={field.placeholder || 'domain.com'}
              maxLength={field.maxLength}
              prefix={<span className="font-mono text-xs whitespace-nowrap">https://</span>}
              onChange={(e) => {
                const raw = e.target.value.replace(/^https?:\/\//, '');
                const { url, suggestion } = normalizeUrl(raw);
                setForm((prev) => ({ ...prev, [field.name]: url }));
                setUrlSuggestions((prev) => ({ ...prev, [field.name]: suggestion }));
                if (suggestion) toast.info(suggestion, { duration: 4000 });
              }}
            />
            <div className="text-xs text-muted-foreground">
              <strong className="font-semibold">Example:</strong> Enter <code className="bg-secondary px-1.5 py-0.5 rounded">domain.com</code> → Saves as <code className="bg-secondary px-1.5 py-0.5 rounded">https://domain.com</code>
            </div>
            {urlSuggestions[field.name] && (
              <div className="bg-info-light border border-info/20 text-info p-2.5 rounded-lg text-xs">
                ℹ️ {urlSuggestions[field.name]}
              </div>
            )}
          </div>
        );
      case 'switch':
        return (
          <div className="flex items-center gap-2">
            <Switch
              checked={value !== false}
              onCheckedChange={(checked) => setForm((prev) => ({ ...prev, [field.name]: checked }))}
            />
            <span className="text-sm text-muted-foreground">{field.label}</span>
          </div>
        );
      default:
        return (
          <Input
            type="text"
            value={value ?? ''}
            placeholder={field.placeholder}
            maxLength={field.maxLength}
            onChange={(e) => setForm((prev) => ({ ...prev, [field.name]: e.target.value }))}
          />
        );
    }
  };

  if (loading) return <PageLoader label={`Loading ${itemLabel}s…`} />;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
        <div>
          <h2 className="page-title">{title}</h2>
          <p className="text-sm text-muted-foreground mt-0.5">{description}</p>
        </div>
        <Button onClick={openCreate} className="flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add {itemLabel}
        </Button>
      </div>

      <SectionTextSettingsCard sectionKey={sectionKey} />

      {items.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <Plus className="w-full h-full" />
          </div>
          <h3 className="empty-state-title">No {itemLabel}s yet</h3>
          <p className="empty-state-description">{emptyHint}</p>
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="w-10">Order</TableHead>
                {columns.map((col) => (
                  <TableHead key={col.name}>{col.label}</TableHead>
                ))}
                {badge && <TableHead>Source</TableHead>}
                {toggleField && <TableHead>Website</TableHead>}
                <TableHead className="w-24">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="text-left">
              <SortableContext items={paginatedItems.map((it) => it._id)} strategy={verticalListSortingStrategy}>
                {paginatedItems.map((item, idx) => (
                  <SortableRow
                    key={item._id}
                    item={item}
                    index={idx}
                    columns={columns}
                    badge={badge}
                    toggleField={toggleField}
                    canDelete={canDelete}
                    deleteDisabledReason={deleteDisabledReason}
                    itemLabel={itemLabel}
                    togglingId={togglingId}
                    onEdit={openEdit}
                    onDelete={setDeleteTarget}
                    onToggle={handleToggleActive}
                  />
                ))}
              </SortableContext>
            </TableBody>
          </Table>
          <Pagination {...paginationProps} itemLabel={`${itemLabel}s`} />
        </DndContext>
      )}

      {/* Add / edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${itemLabel}` : `Add ${itemLabel}`}</DialogTitle>
            <DialogDescription>
              {editing ? `Update this ${itemLabel}'s details.` : `This ${itemLabel} will appear on your website's ${title.toLowerCase()} section.`}
            </DialogDescription>
          </DialogHeader>
          {/* NOTE: no nested scroll container here — a `max-h/overflow-y-auto`
              wrapper clips the focused inputs' borders/rings at its edges.
              DialogContent already scrolls (max-h-[90vh] + p-6 padding), same
              as the gallery "Add Image" modal. */}
          <div className="space-y-5">
            {fields.map((field) => (
              <div key={field.name} className="form-group">
                {field.type !== 'switch' && (
                  <Label className="form-label">
                    {field.label}
                    {field.required && <span className="text-destructive ml-1">*</span>}
                  </Label>
                )}
                {renderField(field)}
                {field.helpText && field.type !== 'image' && (
                  <p className="text-xs text-muted-foreground">{field.helpText}</p>
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save Changes' : `Add ${itemLabel}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={`Delete ${itemLabel}?`}
        description={`This ${itemLabel} will be removed from your website. This action cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={handleDelete}
      />
    </div>
  );
};

// ── Sortable table row (dnd-kit, same pattern as ShowcaseManager) ───────────

interface SortableRowProps {
  item: any;
  index: number;
  columns: ManagerColumnDef[];
  badge?: ManagementSectionManagerProps['badge'];
  toggleField?: ManagementSectionManagerProps['toggleField'];
  canDelete?: (item: any) => boolean;
  deleteDisabledReason?: string;
  itemLabel: string;
  togglingId: string | null;
  onEdit: (item: any) => void;
  onDelete: (item: any) => void;
  onToggle: (id: string) => void;
}

const SortableRow: React.FC<SortableRowProps> = ({
  item,
  index,
  columns,
  badge,
  toggleField,
  canDelete,
  deleteDisabledReason,
  itemLabel,
  togglingId,
  onEdit,
  onDelete,
  onToggle,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item._id });
  const rowBadge = badge?.(item) || null;
  const deletable = canDelete ? canDelete(item) : true;
  // System-wide convention: checked = Active = visible on the website. The
  // server maps the underlying field (isActive / isHidden) to `isActive`.
  const toggleOn = item.isActive !== false;
  const toggling = togglingId === item._id;

  return (
    <TableRow
      ref={setNodeRef}
      style={{
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        transition,
        backgroundColor: isDragging ? 'rgba(0, 0, 0, 0.05)' : undefined,
      }}
      className="hover:bg-muted/50 transition-colors"
    >
      <TableCell className="w-10 select-none cursor-grab">
        <div className="flex items-center gap-2" {...attributes} {...listeners}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground">
            <circle cx="9" cy="12" r="1"/><circle cx="9" cy="5" r="1"/><circle cx="9" cy="19" r="1"/>
            <circle cx="15" cy="12" r="1"/><circle cx="15" cy="5" r="1"/><circle cx="15" cy="19" r="1"/>
          </svg>
          <span className="w-4 text-muted-foreground font-medium">{index + 1}</span>
        </div>
      </TableCell>
      {columns.map((col) => (
        <TableCell key={col.name}>
          {col.render ? col.render(item) : (item[col.name] ?? '—')}
        </TableCell>
      ))}
      {rowBadge && (
        <TableCell>
          <span
            className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap ${
              rowBadge.tone === 'muted'
                ? 'bg-muted text-muted-foreground'
                : 'bg-primary/10 text-primary'
            }`}
          >
            {rowBadge.label}
          </span>
        </TableCell>
      )}
      {toggleField && (
        <TableCell>
          <div className="flex items-center space-x-2">
            <Switch
              checked={toggleOn}
              onCheckedChange={() => onToggle(item._id)}
              disabled={toggling}
              aria-label={`Toggle ${itemLabel} active state`}
            />
            <span className={toggleOn ? 'status-label-active' : 'status-label-inactive'}>
              {toggleOn ? 'Active' : 'Inactive'}
            </span>
          </div>
        </TableCell>
      )}
      <TableCell>
        <div className="flex gap-2">
          <Button variant="outline" size="icon-sm" onClick={() => onEdit(item)} aria-label="Edit">
            <Pencil className="w-4 h-4" />
          </Button>
          <Button
            variant="destructive"
            size="icon-sm"
            disabled={!deletable}
            title={deletable ? undefined : deleteDisabledReason}
            onClick={() => deletable && onDelete(item)}
            aria-label="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
};

export default ManagementSectionManager;
